import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield, faPlus, faTrash, faEdit, faSave, faTimes,
  faSpinner, faCheckCircle, faExclamationTriangle, faMagic, faEye, faEyeSlash, faCopy, faFlask, faCog, faSync,
  faTimeline, faHandPointer, faQrcode, faClone, faSyncAlt, faWifi, faPen, faPaste
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';
import authProfileService from '../../services/authProfileService';
import AuthProfileExtractorModal from './AuthProfileExtractorModal';
import CreateProfileFromCaptureModal from './CreateProfileFromCaptureModal';
import AuthProfileTokenStatus from './AuthProfileTokenStatus';
import LoginDetectionSettings from './LoginDetectionSettings';
import LoginWorkflowViewer from './LoginWorkflowViewer';
import ManualCredentialMapper from './ManualCredentialMapper';
import TOTPConfiguration from './TOTPConfiguration';
import ProfileMergeConfirm from './ProfileMergeConfirm';
import AuthProfileOverrideToggle from './AuthProfileOverrideToggle';
import AuthProfileStalenessIndicator from './AuthProfileStalenessIndicator';

// Must match backend AuthProfile.PALETTE.
const PROFILE_PALETTE = ['#2563EB', '#DC2626', '#16A34A', '#EA580C', '#9333EA', '#0891B2', '#CA8A04', '#DB2777', '#65A30D', '#7C3AED'];

const AUTH_TYPES = [
  { value: 'basic', label: 'Basic Auth', fields: ['username', 'password'] },
  { value: 'bearer', label: 'Bearer Token', fields: ['token'] },
  { value: 'jwt', label: 'JWT', fields: ['token'] },
  { value: 'api_key', label: 'API Key', fields: ['headerName', 'apiKey'] },
  { value: 'cookie', label: 'Cookie', fields: ['cookieString'] },
  { value: 'oauth2', label: 'OAuth2', fields: ['tokenUrl', 'clientId', 'clientSecret', 'scope', 'grantType'] },
  { value: 'form_login', label: 'Form Login', fields: ['loginUrl', 'username', 'password', 'usernameField', 'passwordField'] },
  { value: 'custom_header', label: 'Custom Header', fields: ['headerName', 'headerValue'] },
  { value: 'ntlm', label: 'NTLM', fields: ['username', 'password', 'domain'] },
  { value: 'digest', label: 'Digest', fields: ['username', 'password'] },
  { value: 'proxy_capture', label: 'Proxy Captured', fields: [] }
];

const AuthProfileManager = ({ webTarget, isDarkMode }) => {
  const [profiles, setProfiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(null);
  const [showExtractorModal, setShowExtractorModal] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [showLoginSettings, setShowLoginSettings] = useState(false);
  const [form, setForm] = useState({
    name: '', role: 'authenticated', authType: 'bearer', config: {}, automatedLogin: { enabled: false }
  });
  const [capturingSession, setCapturingSession] = useState(null); // { profileId, polling: true/false }
  const [captureStatus, setCaptureStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Workflow-related state
  const [showWorkflowViewer, setShowWorkflowViewer] = useState(false);
  const [workflowViewerProfileId, setWorkflowViewerProfileId] = useState(null);
  const [showCredentialMapper, setShowCredentialMapper] = useState(false);
  const [credentialMapperProfileId, setCredentialMapperProfileId] = useState(null);
  const [showTOTPConfig, setShowTOTPConfig] = useState(false);
  const [totpProfileId, setTOTPProfileId] = useState(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  useEffect(() => {
    if (webTarget?.project) loadProfiles();
  }, [webTarget]);

  const loadProfiles = async () => {
    try {
      const data = await authProfileService.getAll(webTarget.project);
      setProfiles(data);
    } catch {}
  };

  const resetForm = () => {
    setForm({ name: '', role: 'authenticated', authType: 'bearer', config: {}, automatedLogin: { enabled: false } });
    setEditing(null);
  };

  const handleEdit = (profile) => {
    setEditing(profile._id);

    // DEBUG: Log the full profile to see what data we have
    console.log('=== EDITING PROFILE ===');
    console.log('Profile ID:', profile._id);
    console.log('Auth Type:', profile.authType);
    console.log('config:', profile.config);
    console.log('proxyCaptured:', profile.proxyCaptured);

    // Handle backward compatibility: extract credentials from all possible sources
    const config = { ...(profile.config || {}) };

    // For cookie auth: build cookieString from multiple possible sources
    if (profile.authType === 'cookie' || profile.authType === 'proxy_capture') {
      console.log('Extracting cookies...');
      console.log('  config.cookieString:', config.cookieString);
      console.log('  config.cookies:', config.cookies);
      console.log('  proxyCaptured.authArtifacts.cookies:', profile.proxyCaptured?.authArtifacts?.cookies);

      if (!config.cookieString) {
        // Try config.cookies array first
        if (config.cookies?.length > 0) {
          config.cookieString = config.cookies
            .map(c => `${c.name}=${c.value}`)
            .join('; ');
          console.log('  ✅ Built from config.cookies:', config.cookieString);
        }
        // Try proxyCaptured.authArtifacts.cookies
        else if (profile.proxyCaptured?.authArtifacts?.cookies?.length > 0) {
          config.cookieString = profile.proxyCaptured.authArtifacts.cookies
            .map(c => `${c.name}=${c.value}`)
            .join('; ');
          console.log('  ✅ Built from proxyCaptured.authArtifacts.cookies:', config.cookieString);
        }
        else {
          console.log('  ❌ No cookies found in any location!');
        }
      }
      // Ensure the cookie TABLE has an array to render. Some profiles (esp.
      // proxy_capture) store cookies only under proxyCaptured.authArtifacts, so
      // config.cookies would be empty and the table would show nothing.
      if (!(config.cookies?.length) && profile.proxyCaptured?.authArtifacts?.cookies?.length) {
        config.cookies = profile.proxyCaptured.authArtifacts.cookies.map(c => ({
          name: c.name, value: c.value, domain: c.domain, path: c.path
        }));
      }
      if (!config.cookieString && config.cookies?.length) {
        config.cookieString = config.cookies.map(c => `${c.name}=${c.value}`).join('; ');
      }
    }

    // For JWT/Bearer: extract token from multiple possible sources
    if (profile.authType === 'jwt' || profile.authType === 'bearer') {
      console.log('Extracting token...');
      console.log('  config.token:', config.token);
      console.log('  proxyCaptured.authArtifacts.authorizationHeader:', profile.proxyCaptured?.authArtifacts?.authorizationHeader);

      if (!config.token) {
        // Try proxyCaptured.authArtifacts.authorizationHeader
        if (profile.proxyCaptured?.authArtifacts?.authorizationHeader) {
          const authHeader = profile.proxyCaptured.authArtifacts.authorizationHeader;
          // Extract token (remove "Bearer " prefix if present)
          config.token = authHeader.startsWith('Bearer ')
            ? authHeader.substring(7)
            : authHeader;
          console.log('  ✅ Extracted from authorizationHeader:', config.token.substring(0, 50) + '...');
        }
        else {
          console.log('  ❌ No token found in any location!');
        }
      }
    }

    console.log('Final config:', config);

    setForm({
      name: profile.name,
      role: profile.role,
      authType: profile.authType,
      config: config,
      automatedLogin: profile.automatedLogin || { enabled: false },
      proxyCaptured: profile.proxyCaptured,
      updateBehaviorOverride: profile.updateBehaviorOverride,
      color: profile.color || '#2563EB',
      colorLocked: profile.colorLocked || false
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const savedProfileId = editing && editing !== 'new' ? editing : null;

      if (editing && editing !== 'new') {
        await authProfileService.update(editing, { ...form, project: webTarget.project });
        toast.success('Profile updated successfully!');
      } else {
        const created = await authProfileService.create({ ...form, project: webTarget.project });
        toast.success('Profile created successfully!');
        setEditing(created._id); // Keep editing the newly created profile
      }

      await loadProfiles();

      // If we saved an existing profile, keep it open by reloading its data
      if (savedProfileId) {
        const updatedProfile = await authProfileService.getById(savedProfileId);
        if (updatedProfile) {
          setForm(updatedProfile);
          setEditing(savedProfileId);
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(`Failed to save profile: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await authProfileService.remove(id);
      loadProfiles();
    } catch {}
  };

  const [syncing, setSyncing] = useState(false);
  const handleSyncCookies = async () => {
    if (!editing || editing === 'new' || !webTarget?._id) return;
    setSyncing(true);
    const t = toast.loading('Syncing session from live traffic…');
    try {
      const res = await authProfileService.syncCookiesFromTraffic(editing, webTarget._id);
      toast.update(t, {
        render: `Synced ${res.count} cookie(s)${res.matchedByIdentity ? ' (matched to this user)' : ''}: ${res.synced.slice(0, 6).join(', ')}${res.synced.length > 6 ? '…' : ''}`,
        type: 'success', isLoading: false, autoClose: 7000
      });
      const updated = await authProfileService.getById(editing);
      if (updated) handleEdit(updated);
      loadProfiles();
    } catch (err) {
      toast.update(t, { render: 'Sync failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 9000 });
    } finally {
      setSyncing(false);
    }
  };

  // Editable cookie helpers — keep config.cookies and cookieString in sync so
  // edits/deletes persist on Save and are used by the replay engine.
  const rebuildCookieString = (cookies) => (cookies || []).map(c => `${c.name}=${c.value}`).join('; ');
  const updateCookieValue = (idx, value) => {
    setForm(p => {
      const cookies = (p.config.cookies || []).map((c, i) => i === idx ? { ...c, value } : c);
      return { ...p, config: { ...p.config, cookies, cookieString: rebuildCookieString(cookies) } };
    });
  };
  const deleteCookie = (idx) => {
    setForm(p => {
      const removed = p.config.cookies?.[idx];
      const cookies = (p.config.cookies || []).filter((_, i) => i !== idx);
      const cfg = { ...p.config, cookies, cookieString: rebuildCookieString(cookies) };
      if (removed && p.config.sessionCookieName === removed.name) cfg.sessionCookieName = undefined;
      return { ...p, config: cfg };
    });
  };
  // Paste a cookie string (name=value; …) — update existing cookie values,
  // create missing ones. Accepts ';' or newline separators.
  const pasteCookies = async () => {
    let text = '';
    try { text = await navigator.clipboard.readText(); } catch { /* clipboard blocked */ }
    if (!text || !text.trim()) {
      text = window.prompt('Paste cookies (format: name=value; name2=value2):') || '';
    }
    if (!text.trim()) return;
    const parsed = [];
    for (const part of text.split(/[;\n]+/)) {
      const s = part.trim();
      const i = s.indexOf('=');
      if (i <= 0) continue;
      parsed.push({ name: s.slice(0, i).trim(), value: s.slice(i + 1).trim() });
    }
    if (!parsed.length) { toast.error('No valid cookies found in the pasted text'); return; }
    setForm(p => {
      const cookies = [...(p.config.cookies || [])];
      let updated = 0, added = 0;
      for (const pc of parsed) {
        const idx = cookies.findIndex(c => c.name === pc.name);
        if (idx >= 0) { cookies[idx] = { ...cookies[idx], value: pc.value }; updated++; }
        else { cookies.push({ name: pc.name, value: pc.value }); added++; }
      }
      toast.success(`Pasted: ${updated} updated, ${added} added`);
      return { ...p, config: { ...p.config, cookies, cookieString: rebuildCookieString(cookies) } };
    });
  };

  const startRename = (e, profile) => {
    e.stopPropagation();
    setRenamingId(profile._id);
    setRenameValue(profile.name || '');
  };
  const cancelRename = () => { setRenamingId(null); setRenameValue(''); };
  const commitRename = async (id) => {
    const name = renameValue.trim();
    if (!name) { cancelRename(); return; }
    try {
      await authProfileService.update(id, { name, project: webTarget.project });
      setRenamingId(null); setRenameValue('');
      loadProfiles();
      if (editing === id) setForm(f => ({ ...f, name }));
      toast.success('Profile renamed');
    } catch (err) {
      toast.error(`Rename failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleCopy = async (id) => {
    try {
      const response = await axios.post(`/auth-profiles/${id}/copy`);
      toast.success(`Profile copied! Update credentials and test login.`);
      loadProfiles();
      // Auto-select the copied profile for editing
      setEditing(response.data._id);
    } catch (err) {
      toast.error(`Failed to copy profile: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleTestLoginAndCapture = async () => {
    if (!editing) return;

    setLoading(true);
    try {
      const response = await axios.post(`/auth-profiles/${editing}/test-login-and-capture`);

      if (response.data.success) {
        toast.success(response.data.message);
        loadProfiles();

        // Show captured data summary
        const { capturedData } = response.data;
        if (capturedData) {
          toast.info(
            `Captured: ${capturedData.cookies} cookies, ${capturedData.tokens} tokens` +
            (capturedData.sessionCookieName ? `, Session: ${capturedData.sessionCookieName}` : ''),
            { autoClose: 5000 }
          );
        }
      } else {
        toast.error(response.data.error || 'Login failed');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      toast.error(`Login failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (id) => {
    setValidating(id);
    try {
      const result = await authProfileService.validate(id);
      setProfiles(prev => prev.map(p => p._id === id ? { ...p, _validationResult: result } : p));
    } catch {} finally {
      setValidating(null);
    }
  };

  const updateConfig = (key, value) => {
    setForm(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));
  };

  const handleLaunchBrowser = async () => {
    if (!form.automatedLogin?.loginUrl) {
      toast.error('Please enter a Login URL first');
      return;
    }

    try {
      const profileId = editing === 'new' ? null : editing;
      if (!profileId) {
        toast.error('Please save the profile first before launching browser');
        return;
      }

      const response = await axios.post(`/auth-profiles/${profileId}/launch-browser`, {
        startUrl: form.automatedLogin.loginUrl
      });

      if (response.data.success) {
        toast.success('🌐 Browser launched! Please log in manually. Credentials will be captured automatically.');
        setCapturingSession({ profileId, polling: true });

        // Start polling for captured credentials
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`/auth-profiles/${profileId}/capture-status`);
            setCaptureStatus(statusRes.data);

            // If we have captured data, notify user
            if (statusRes.data.hasCapturedData && !statusRes.data.notified) {
              toast.info(`✅ Captured ${statusRes.data.cookiesCount} cookies and ${statusRes.data.tokensCount} tokens!`);
              statusRes.data.notified = true;
            }
          } catch (error) {
            console.error('Polling error:', error);
          }
        }, 3000);

        setCapturingSession(prev => ({ ...prev, pollInterval }));
      }
    } catch (error) {
      console.error('Launch browser error:', error);
      toast.error('Failed to launch browser: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFinalizeCapture = async () => {
    if (!capturingSession) return;

    try {
      const response = await axios.post(`/auth-profiles/${capturingSession.profileId}/finalize-capture`);

      if (response.data.success) {
        if (response.data.learnedLogin) {
          toast.success('🧠 Login learned! AutoPlay enabled with username "' + response.data.learnedCredentials.username + '"', {
            autoClose: 5000
          });
        } else {
          toast.success('✅ Credentials saved to profile!');
        }

        // Clear polling
        if (capturingSession.pollInterval) {
          clearInterval(capturingSession.pollInterval);
        }
        setCapturingSession(null);
        setCaptureStatus(null);

        // Reload profiles to show updated data
        await loadProfiles();

        // Re-edit the profile to show updated data
        const updatedProfile = await authProfileService.getById(capturingSession.profileId);
        handleEdit(updatedProfile);
      }
    } catch (error) {
      console.error('Finalize capture error:', error);
      toast.error('Failed to save credentials: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelCapture = async () => {
    if (!capturingSession) return;

    try {
      await axios.post(`/auth-profiles/${capturingSession.profileId}/close-session`);

      if (capturingSession.pollInterval) {
        clearInterval(capturingSession.pollInterval);
      }
      setCapturingSession(null);
      setCaptureStatus(null);
      toast.info('Browser capture cancelled');
    } catch (error) {
      console.error('Cancel capture error:', error);
    }
  };

  const handleTokenExtracted = async (token) => {
    // Auto-configure form from extracted token
    const profileName = `Extracted ${token.authType} - ${token.name}`;

    // Map auth type and build config
    let authType = token.authType;
    let config = {};

    switch (token.authType) {
      case 'jwt':
      case 'bearer':
        authType = 'bearer';
        config = { token: token.value };
        break;
      case 'cookie':
        authType = 'cookie';
        config = { cookieString: `${token.name}=${token.value}` };
        break;
      case 'api_key':
        authType = 'api_key';
        config = { headerName: token.name, apiKey: token.value };
        break;
      case 'csrf':
        authType = 'custom_header';
        config = { headerName: 'X-CSRF-Token', headerValue: token.value };
        break;
      case 'custom_header':
        authType = 'custom_header';
        config = { headerName: token.name, headerValue: token.value };
        break;
      default:
        authType = 'custom_header';
        config = { headerName: token.name, headerValue: token.value };
    }

    setForm({
      name: profileName,
      role: 'user',
      authType: authType,
      config: config
    });
    setEditing('new');
  };

  // Workflow-related handlers
  const handleViewWorkflow = (profileId) => {
    setWorkflowViewerProfileId(profileId);
    setShowWorkflowViewer(true);
  };

  const handleOpenCredentialMapper = (profileId) => {
    setCredentialMapperProfileId(profileId);
    setShowCredentialMapper(true);
  };

  const handleOpenTOTPConfig = (profileId) => {
    setTOTPProfileId(profileId);
    setShowTOTPConfig(true);
  };

  const handleCloneWithCredentials = async (profileId) => {
    const newUsername = prompt('Enter username for the new profile:');
    const newPassword = prompt('Enter password for the new profile:');

    if (!newUsername || !newPassword) {
      toast.warning('Credentials required for cloning');
      return;
    }

    try {
      const response = await axios.post(`/auth-profiles/${profileId}/clone-with-credentials`, {
        username: newUsername,
        password: newPassword
      });

      if (response.data.success) {
        toast.success(`✓ Profile cloned with new credentials!`);
        await loadProfiles();
      }
    } catch (error) {
      console.error('Clone failed:', error);
      toast.error('Failed to clone profile: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRefreshSession = async (profileId) => {
    try {
      const response = await axios.post(`/auth-profiles/${profileId}/refresh-session`);

      if (response.data.success) {
        toast.success('✓ Session refreshed successfully!');
        await loadProfiles();

        // Re-edit the profile to show updated data
        const updatedProfile = await authProfileService.getById(profileId);
        handleEdit(updatedProfile);
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      toast.error('Failed to refresh session: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleMappingComplete = async () => {
    await loadProfiles();
    if (credentialMapperProfileId) {
      const updatedProfile = await authProfileService.getById(credentialMapperProfileId);
      handleEdit(updatedProfile);
    }
  };

  const handleDuplicateResolved = async () => {
    await loadProfiles();
  };

  const typeInfo = AUTH_TYPES.find(t => t.value === form.authType) || AUTH_TYPES[1];
  const inputClass = `w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;
  const labelClass = `text-[10px] font-medium block mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faUserShield} className="text-indigo-400" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Auth Profiles ({profiles.length})
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowLoginSettings(true)}
            className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            title="Configure login detection parameters">
            <FontAwesomeIcon icon={faCog} /> Detection Settings
          </button>
          <button onClick={() => setShowCaptureModal(true)}
            className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-green-700 text-white hover:bg-green-600' : 'bg-green-600 text-white hover:bg-green-700'}`}
            title="Create a profile from a login you already captured through the proxy">
            <FontAwesomeIcon icon={faWifi} /> From Login Capture
          </button>
          <button onClick={() => setShowExtractorModal(true)}
            className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-purple-700 text-white hover:bg-purple-600' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>
            <FontAwesomeIcon icon={faMagic} /> Extract Token
          </button>
          <button onClick={() => { resetForm(); setEditing('new'); }}
            className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 flex items-center gap-1">
            <FontAwesomeIcon icon={faPlus} /> New Profile
          </button>
          <button onClick={async () => {
              try {
                toast.info('Assessing login outcomes...');
                await axios.post('/auth-profiles/assess-login-outcomes', { projectId: webTarget.project });
                await loadProfiles();
                toast.success('Login outcomes updated');
              } catch (e) { toast.error('Assessment failed: ' + (e.response?.data?.error || e.message)); }
            }}
            className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            title="Assess login outcomes for all detected profiles">
            <FontAwesomeIcon icon={faFlask} /> Assess
          </button>
          <button onClick={loadProfiles}
            className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            title="Reload profiles">
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Profile List */}
        <div className={`w-64 border-r overflow-y-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {profiles.map(profile => (
            <div key={profile._id}
              onClick={() => handleEdit(profile)}
              className={`px-3 py-2 cursor-pointer border-b ${
                editing === profile._id
                  ? (isDarkMode ? 'bg-indigo-900/20 border-gray-700' : 'bg-indigo-50 border-gray-200')
                  : (isDarkMode ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50')
              }`}>
              <div className="flex items-center justify-between">
                {renamingId === profile._id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(profile._id); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                    }}
                    onBlur={() => commitRename(profile._id)}
                    className={`text-xs font-medium flex-1 mr-2 px-1 py-0.5 rounded border ${
                      isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
                    }`}
                  />
                ) : (
                  <span className={`text-xs font-medium truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {profile.name}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={(e) => startRename(e, profile)}
                    className="text-gray-400 hover:text-gray-200 text-[10px]"
                    title="Rename profile">
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleCopy(profile._id); }}
                    className="text-blue-400 hover:text-blue-300 text-[10px]"
                    title="Copy profile (clone with same login settings)">
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(profile._id); }}
                    className="text-red-400 hover:text-red-300 text-[10px]">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  {profile.authType}
                </span>
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{profile.role}</span>
                {profile.detectionMetadata?.loginOutcome && (
                  <span title={profile.detectionMetadata.loginOutcomeReason || ''}
                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      profile.detectionMetadata.loginOutcome === 'success'
                        ? (isDarkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700')
                        : profile.detectionMetadata.loginOutcome === 'partial'
                        ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                        : profile.detectionMetadata.loginOutcome === 'failed'
                        ? (isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700')
                        : (isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400')
                    }`}>
                    {profile.detectionMetadata.loginOutcome}
                  </span>
                )}
              </div>
              {profile._validationResult && (
                <div className="mt-1">
                  <FontAwesomeIcon
                    icon={profile._validationResult.valid ? faCheckCircle : faExclamationTriangle}
                    className={`text-[10px] ${profile._validationResult.valid ? 'text-green-400' : 'text-yellow-400'}`}
                  />
                </div>
              )}
              {(profile.authType === 'jwt' || profile.authType === 'bearer') && (
                <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                  <AuthProfileTokenStatus profile={profile} compact={true} onRefreshed={loadProfiles} />
                </div>
              )}
              {/* Staleness indicator (Phase 9) */}
              <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                <AuthProfileStalenessIndicator profile={profile} isDarkMode={isDarkMode} size="small" />
              </div>
            </div>
          ))}
          {profiles.length === 0 && (
            <div className={`p-4 text-xs text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              No auth profiles. Create one to enable authenticated testing.
            </div>
          )}
        </div>

        {/* Edit Form */}
        <div className="flex-1 overflow-auto">
          {editing ? (
            <div className="p-4 space-y-3">
              <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {editing === 'new' ? 'New Auth Profile' : 'Edit Auth Profile'}
              </h3>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Name</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className={inputClass} placeholder="Admin user" />
                </div>
                <div>
                  <label className={labelClass}>Role</label>
                  <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className={inputClass}>
                    <option value="unauthenticated">Unauthenticated</option>
                    <option value="authenticated">Authenticated</option>
                    <option value="admin">Admin</option>
                    <option value="privileged">Privileged</option>
                    <option value="low_privilege">Low Privilege</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Auth Type</label>
                  <select value={form.authType} onChange={e => setForm(p => ({ ...p, authType: e.target.value, config: {} }))} className={inputClass}>
                    {AUTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Profile color — used for the identity dots in Traffic + site tree */}
              <div className="flex items-center gap-2">
                <label className={labelClass}>Color</label>
                <span className="w-3 h-3 rounded-full border border-gray-500" style={{ backgroundColor: form.color || '#2563EB' }} />
                <div className="flex items-center gap-1">
                  {PROFILE_PALETTE.map(c => (
                    <button key={c} type="button"
                      onClick={() => setForm(p => ({ ...p, color: c, colorLocked: true }))}
                      className={`w-4 h-4 rounded-full border ${form.color === c ? 'ring-2 ring-offset-1 ring-teal-400 border-white' : 'border-gray-500'}`}
                      style={{ backgroundColor: c }}
                      title={c} />
                  ))}
                </div>
              </div>

              {/* Dynamic config fields */}
              <div className={`p-3 rounded-lg border space-y-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {typeInfo.label} Configuration
                </div>

                {/* Cookie / proxy-capture auth - show the stored cookies as a table */}
                {(form.authType === 'cookie' || form.authType === 'proxy_capture') && form.config.cookies?.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Cookies ({form.config.cookies.length})
                      </div>
                      <div className={`text-[9px] ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        ← Mark session cookie for AuthSmash
                      </div>
                    </div>
                    <div className={`overflow-auto max-h-48 rounded border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      <table className="w-full text-[10px]">
                        <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <tr>
                            <th className={`px-2 py-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} title="Mark as session cookie">
                              🔑
                            </th>
                            <th className={`px-2 py-1 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name</th>
                            <th className={`px-2 py-1 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Value</th>
                            <th className={`px-2 py-1 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Domain</th>
                            <th className={`px-2 py-1 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Path</th>
                            <th className={`px-2 py-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} title="Delete cookie"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.config.cookies.map((cookie, idx) => {
                            const isSessionCookie = form.config.sessionCookieName === cookie.name;
                            return (
                              <tr
                                key={idx}
                                className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${
                                  isSessionCookie ? (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''
                                }`}
                              >
                                <td className="px-2 py-1 text-center">
                                  <input
                                    type="radio"
                                    name="sessionCookie"
                                    checked={isSessionCookie}
                                    onChange={() => {
                                      setForm(p => ({
                                        ...p,
                                        config: { ...p.config, sessionCookieName: cookie.name }
                                      }));
                                    }}
                                    className="cursor-pointer"
                                    title="Mark as session cookie"
                                  />
                                </td>
                                <td className={`px-2 py-1 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {cookie.name}
                                  {isSessionCookie && (
                                    <span className={`ml-1 text-[9px] ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      (session)
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={cookie.value}
                                    onChange={(e) => updateCookieValue(idx, e.target.value)}
                                    title={cookie.value}
                                    className={`w-full font-mono text-[10px] px-1 py-0.5 rounded border ${
                                      isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-300 focus:border-teal-500' : 'bg-white border-gray-300 text-gray-700 focus:border-teal-500'
                                    }`}
                                  />
                                </td>
                                <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {cookie.domain || '-'}
                                </td>
                                <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {cookie.path || '/'}
                                </td>
                                <td className="px-2 py-1 text-center">
                                  <button type="button" onClick={() => deleteCookie(idx)}
                                    className="text-red-400 hover:text-red-300 text-[10px]" title="Delete this cookie">
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-[9px] space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Cookie string ({form.config.cookies.length}):</span>
                        <div className="flex items-center gap-2">
                          <button type="button"
                            onClick={pasteCookies}
                            className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-teal-800 text-teal-200 hover:bg-teal-700' : 'bg-teal-100 text-teal-700 hover:bg-teal-200'}`}
                            title="Paste cookies (name=value; …) — updates existing values, adds missing ones">
                            <FontAwesomeIcon icon={faPaste} /> Paste
                          </button>
                          <button type="button"
                            onClick={() => { navigator.clipboard.writeText(form.config.cookieString || ''); toast.success('Cookie header copied'); }}
                            className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                            title="Copy the full Cookie: header value (for curl / Repeater / a cookie editor)">
                            <FontAwesomeIcon icon={faCopy} /> Cookie header
                          </button>
                          <button type="button"
                            onClick={() => {
                              const js = (form.config.cookies || [])
                                .map(c => `document.cookie=${JSON.stringify(`${c.name}=${c.value}; path=${c.path || '/'}`)};`)
                                .join('\n');
                              navigator.clipboard.writeText(js);
                              toast.success('JS snippet copied (note: HttpOnly cookies can’t be set from JS)');
                            }}
                            className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                            title="Copy a document.cookie JS snippet to paste in the browser console (non-HttpOnly cookies only)">
                            <FontAwesomeIcon icon={faCopy} /> JS
                          </button>
                        </div>
                      </div>
                      <div className={`font-mono ${isDarkMode ? 'text-gray-600 bg-gray-800' : 'text-gray-400 bg-gray-100'} p-2 rounded overflow-x-auto max-h-20 text-[8px]`}>
                        <code className="whitespace-pre-wrap break-all">
                          {form.config.cookieString}
                        </code>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Regular fields for non-cookie auth types */
                  typeInfo.fields.map(field => (
                    <div key={field}>
                      <label className={labelClass}>{field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
                      {field === 'grantType' ? (
                        <select value={form.config[field] || 'client_credentials'} onChange={e => updateConfig(field, e.target.value)} className={inputClass}>
                          <option value="client_credentials">Client Credentials</option>
                          <option value="password">Password</option>
                          <option value="authorization_code">Authorization Code</option>
                        </select>
                      ) : field === 'token' && form.config[field]?.length > 100 ? (
                        /* Show long tokens in a textarea */
                        <textarea
                          value={form.config[field] || ''}
                          onChange={e => updateConfig(field, e.target.value)}
                          className={inputClass + ' font-mono'}
                          rows={3}
                          placeholder={field}
                        />
                      ) : (
                        <input
                          type="text"
                          value={form.config[field] || ''}
                          onChange={e => updateConfig(field, e.target.value)}
                          className={inputClass}
                          placeholder={field}
                        />
                      )}
                    </div>
                  ))
                )}

                {/* Proxy-capture with no cookies: explain what's stored */}
                {form.authType === 'proxy_capture' && !(form.config.cookies?.length) && (
                  <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    This profile replays a session captured from the proxy. See the captured headers below; no editable cookie list was stored.
                  </div>
                )}
              </div>

              {/* Captured auth headers (read-only) — exactly what the proxy stored */}
              {(form.proxyCaptured?.authArtifacts?.authorizationHeader ||
                form.proxyCaptured?.authArtifacts?.customHeaders?.length > 0) && (
                <div className={`p-3 rounded-lg border space-y-1.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <div className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Captured Headers (read-only)</div>
                  {form.proxyCaptured.authArtifacts.authorizationHeader && (
                    <div className="text-[10px] break-all">
                      <span className={isDarkMode ? 'text-teal-400' : 'text-teal-600'}>Authorization:</span>{' '}
                      <span className="font-mono">
                        {form.proxyCaptured.authArtifacts.authorizationHeader.slice(0, 90)}
                        {form.proxyCaptured.authArtifacts.authorizationHeader.length > 90 ? '…' : ''}
                      </span>
                    </div>
                  )}
                  {(form.proxyCaptured.authArtifacts.customHeaders || []).map((h, i) => (
                    <div key={i} className="text-[10px] break-all">
                      <span className={isDarkMode ? 'text-teal-400' : 'text-teal-600'}>{h.name}:</span>{' '}
                      <span className="font-mono">
                        {String(h.value).slice(0, 90)}{String(h.value).length > 90 ? '…' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Token Refresh */}
              {['bearer', 'jwt', 'oauth2'].includes(form.authType) && (
                <div className={`p-3 rounded-lg border space-y-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.config.autoRefresh || false}
                      onChange={e => updateConfig('autoRefresh', e.target.checked)} className="rounded" />
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Auto-refresh token</span>
                  </label>
                  {form.config.autoRefresh && (
                    <div>
                      <label className={labelClass}>Refresh URL</label>
                      <input value={form.config.refreshUrl || ''} onChange={e => updateConfig('refreshUrl', e.target.value)}
                        className={inputClass} placeholder="https://example.com/oauth/token" />
                    </div>
                  )}
                </div>
              )}

              {/* Update Behavior Override (Phase 9) */}
              <AuthProfileOverrideToggle
                profile={form}
                webTargetDefaultMode={webTarget?.authProfileUpdateBehavior?.mode || 'auto'}
                onChange={(override) => setForm(p => ({ ...p, updateBehaviorOverride: override }))}
                isDarkMode={isDarkMode}
              />

              {/* Automated Login (AutoPlay) */}
              <div className={`p-3 rounded-lg border space-y-2 ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.automatedLogin?.enabled || false}
                      onChange={e => setForm(p => ({
                        ...p,
                        automatedLogin: { ...(p.automatedLogin || {}), enabled: e.target.checked }
                      }))}
                      className="rounded"
                    />
                    <span className={isDarkMode ? 'text-blue-300' : 'text-blue-700'}>
                      🤖 Enable AutoPlay (Automated Login)
                    </span>
                  </label>
                </div>
                <p className={`text-[10px] ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  Automatically authenticate using stored credentials when testing
                </p>
                <p className={`text-[10px] ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  ⚠️ AutoPlay replays username/password only — it cannot complete an MFA / SSO
                  challenge. For MFA logins, rely on the captured session (this profile's cookies/headers)
                  and re-capture via “From Login Capture” when it expires, rather than AutoPlay.
                </p>
                {form.automatedLogin?.enabled && (
                  <div className="space-y-2 pt-2">
                    {/* Show captured credentials info if available */}
                    {editing !== 'new' && (form.automatedLogin?.username || form.automatedLogin?.password) && (
                      <div className={`p-2 rounded text-[10px] ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                        <div className={`font-medium mb-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                          🧠 Learned from Manual Login
                        </div>
                        <div className={`space-y-1 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                          <div>Username: <span className="font-mono">{form.automatedLogin.username}</span></div>
                          <div className="flex items-center gap-2">
                            <span>Password:</span>
                            <span className="font-mono flex-1">
                              {showPassword ? form.automatedLogin.password : '••••••••••••'}
                            </span>
                            <button
                              onClick={() => setShowPassword(!showPassword)}
                              className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'hover:bg-yellow-800' : 'hover:bg-yellow-200'}`}
                            >
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                          </div>
                          {form.automatedLogin.selectors && (
                            <div className="mt-1 pt-1 border-t border-yellow-700/30">
                              <div className="opacity-70">Field selectors detected</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelClass}>Username / Email</label>
                        <input
                          value={form.automatedLogin?.username || ''}
                          onChange={e => setForm(p => ({
                            ...p,
                            automatedLogin: { ...(p.automatedLogin || {}), username: e.target.value }
                          }))}
                          className={inputClass}
                          placeholder="admin@example.com"
                        />
                        {form.automatedLogin?.detectedFieldNames?.usernameField && (
                          <div className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            Field: <span className="font-mono">{form.automatedLogin.detectedFieldNames.usernameField}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>Password</label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={form.automatedLogin?.password || ''}
                            onChange={e => setForm(p => ({
                              ...p,
                              automatedLogin: { ...(p.automatedLogin || {}), password: e.target.value }
                            }))}
                            className={inputClass + ' pr-8'}
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`}
                          >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                          </button>
                        </div>
                        {form.automatedLogin?.detectedFieldNames?.passwordField && (
                          <div className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            Field: <span className="font-mono">{form.automatedLogin.detectedFieldNames.passwordField}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Login URL</label>
                      <input
                        value={form.automatedLogin?.loginUrl || ''}
                        onChange={e => setForm(p => ({
                          ...p,
                          automatedLogin: { ...(p.automatedLogin || {}), loginUrl: e.target.value }
                        }))}
                        className={inputClass}
                        placeholder="https://example.com/login"
                      />
                    </div>

                    {/* Capture Session Controls */}
                    {!capturingSession ? (
                      <div className="space-y-2">
                        <button
                          onClick={handleLaunchBrowser}
                          disabled={editing === 'new'}
                          className={`w-full px-3 py-1.5 rounded text-xs flex items-center justify-center gap-2 ${
                            editing === 'new'
                              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              : isDarkMode
                                ? 'bg-blue-700 text-white hover:bg-blue-600'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          🌐 Launch Browser & Capture Session
                        </button>

                        <div className="flex items-center gap-2">
                          <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                          <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>OR</span>
                          <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                        </div>

                        <button
                          onClick={handleTestLoginAndCapture}
                          disabled={editing === 'new' || !form.automatedLogin?.username || !form.automatedLogin?.password}
                          className={`w-full px-3 py-1.5 rounded text-xs flex items-center justify-center gap-2 ${
                            (editing === 'new' || !form.automatedLogin?.username || !form.automatedLogin?.password)
                              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              : isDarkMode
                                ? 'bg-green-700 text-white hover:bg-green-600'
                                : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                          title="Automated login in headless mode (works on remote servers)"
                        >
                          <FontAwesomeIcon icon={faFlask} />
                          Test Login & Capture (Headless)
                        </button>
                      </div>
                    ) : (
                      <div className={`space-y-2 p-3 rounded border ${isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                        <div className="flex items-center gap-2 text-xs">
                          <FontAwesomeIcon icon={faSpinner} spin className="text-green-500" />
                          <span className={isDarkMode ? 'text-green-300' : 'text-green-700'}>
                            Browser session active - please log in
                          </span>
                        </div>
                        {captureStatus && (
                          <div className={`space-y-1 text-[10px] ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            <div className="flex justify-between">
                              <span>Cookies:</span>
                              <span className="font-bold">{captureStatus.cookiesCount}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Tokens:</span>
                              <span className="font-bold">{captureStatus.tokensCount}</span>
                            </div>
                            {captureStatus.hasLearnedLogin && (
                              <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
                                <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 font-bold">
                                  <span>🧠</span>
                                  <span>Login Learned!</span>
                                </div>
                                <div className="mt-1 text-yellow-700 dark:text-yellow-300">
                                  Username: {captureStatus.learnedUsername}
                                </div>
                              </div>
                            )}
                            {captureStatus.currentUrl && (
                              <div className="truncate mt-1">
                                <span className="opacity-60">URL:</span> {captureStatus.currentUrl}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={handleFinalizeCapture}
                            disabled={!captureStatus?.hasCapturedData}
                            className={`flex-1 px-2 py-1 rounded text-xs font-medium ${
                              captureStatus?.hasCapturedData
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {captureStatus?.hasLearnedLogin ? '✅ Save & Enable AutoPlay' : '✅ Save Credentials'}
                          </button>
                          <button
                            onClick={handleCancelCapture}
                            className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-700'}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Token Status Display (for existing profiles with JWT/Bearer) */}
              {editing !== 'new' && (form.authType === 'jwt' || form.authType === 'bearer') && (
                <AuthProfileTokenStatus
                  profile={profiles.find(p => p._id === editing)}
                  onRefreshed={loadProfiles}
                />
              )}

              <div className="flex gap-2">
                <button onClick={handleSave} disabled={loading || !form.name}
                  className="px-4 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
                  {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />} Save
                </button>
                {editing !== 'new' && (
                  <button onClick={() => handleValidate(editing)} disabled={validating === editing}
                    className={`px-4 py-1.5 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                    {validating === editing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />} Validate
                  </button>
                )}
                {editing !== 'new' && (
                  <button onClick={handleSyncCookies} disabled={syncing}
                    className="px-4 py-1.5 rounded text-xs flex items-center gap-1 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                    title="Pull the current authenticated session (all cookies incl. HttpOnly) from live proxy traffic into this profile">
                    {syncing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSync} />} Sync Session
                  </button>
                )}
                <button onClick={resetForm}
                  className={`px-4 py-1.5 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                  <FontAwesomeIcon icon={faTimes} className="mr-1" /> Cancel
                </button>
              </div>

              {/* Workflow Actions Section */}
              {editing !== 'new' && profiles.find(p => p._id === editing)?.loginWorkflow && (
                <div className={`mt-4 p-3 rounded border ${isDarkMode ? 'bg-gray-750 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
                  <h4 className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FontAwesomeIcon icon={faTimeline} className="mr-1 text-blue-500" />
                    Login Workflow Actions
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {/* View Workflow - always available if workflow exists */}
                    <button
                      onClick={() => handleViewWorkflow(editing)}
                      className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                    >
                      <FontAwesomeIcon icon={faTimeline} /> View Workflow
                    </button>

                    {/* Manual Mapping - show if credentials not extracted */}
                    {!profiles.find(p => p._id === editing)?.loginWorkflow?.credentials?.autoDetected?.usernameField && (
                      <button
                        onClick={() => handleOpenCredentialMapper(editing)}
                        className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-yellow-500 hover:bg-yellow-600'} text-white`}
                      >
                        <FontAwesomeIcon icon={faHandPointer} /> Map Credentials
                      </button>
                    )}

                    {/* TOTP Setup */}
                    <button
                      onClick={() => handleOpenTOTPConfig(editing)}
                      className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white`}
                      title="Configure TOTP/MFA for this profile"
                    >
                      <FontAwesomeIcon icon={faQrcode} /> TOTP Setup
                    </button>

                    {/* Clone with Credentials - show if workflow and credentials exist */}
                    {profiles.find(p => p._id === editing)?.loginWorkflow?.credentials && (
                      <button
                        onClick={() => handleCloneWithCredentials(editing)}
                        className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white`}
                        title="Replay workflow with different credentials to create a new profile"
                      >
                        <FontAwesomeIcon icon={faClone} /> Clone with New Creds
                      </button>
                    )}

                    {/* Refresh Session - show if workflow and credentials exist */}
                    {profiles.find(p => p._id === editing)?.loginWorkflow?.credentials && (
                      <button
                        onClick={() => handleRefreshSession(editing)}
                        className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white`}
                        title="Replay workflow with existing credentials to refresh session"
                      >
                        <FontAwesomeIcon icon={faSyncAlt} /> Refresh Session
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Detection Metadata (for auto-created profiles) - Moved below workflow actions */}
              {editing !== 'new' && profiles.find(p => p._id === editing)?.detectionMetadata && (
                <div className={`mt-4 p-3 rounded-lg border space-y-2 ${isDarkMode ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
                  <div className={`text-xs font-medium flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                    <span>🔍 Auto-Creation Details</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                      profiles.find(p => p._id === editing)?.detectionMetadata?.confidence === 'high'
                        ? 'bg-green-600 text-white'
                        : profiles.find(p => p._id === editing)?.detectionMetadata?.confidence === 'medium'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}>
                      {profiles.find(p => p._id === editing)?.detectionMetadata?.confidence || 'unknown'} confidence
                    </span>
                  </div>
                  <div className={`text-[10px] space-y-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                    {(() => {
                      const metadata = profiles.find(p => p._id === editing)?.detectionMetadata;
                      return (
                        <>
                          <div className="flex items-start gap-2">
                            <span className="font-medium min-w-[100px]">Detected At:</span>
                            <span>{metadata.detectedAt ? new Date(metadata.detectedAt).toLocaleString() : 'Unknown'}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-medium min-w-[100px]">URL Matched:</span>
                            <span className="font-mono break-all">{metadata.matchedUrl || 'N/A'}</span>
                          </div>
                          {metadata.urlPattern && metadata.urlPattern !== 'none' && (
                            <div className="flex items-start gap-2">
                              <span className="font-medium min-w-[100px]">URL Pattern:</span>
                              <span className="font-mono text-[9px]">{metadata.urlPattern}</span>
                            </div>
                          )}
                          <div className="flex items-start gap-2">
                            <span className="font-medium min-w-[100px]">Detection Method:</span>
                            <span>{metadata.detectionMethod || 'unknown'}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-medium min-w-[100px]">Request:</span>
                            <span>{metadata.requestMethod || '?'} → {metadata.responseStatus || '?'}</span>
                          </div>
                          {metadata.triggeredRules && metadata.triggeredRules.length > 0 && (
                            <div className="mt-2">
                              <div className="font-medium mb-1">Triggered Rules:</div>
                              <ul className="list-disc list-inside space-y-0.5 ml-2">
                                {metadata.triggeredRules.map((rule, idx) => (
                                  <li key={idx}>{rule}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className={`text-[9px] pt-1 border-t ${isDarkMode ? 'border-amber-800 text-amber-500' : 'border-amber-300 text-amber-600'}`}>
                    💡 This profile was automatically created based on detected login activity in proxy traffic
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Select a profile to edit or create a new one.
            </div>
          )}
        </div>
      </div>

      {/* Token Extractor Modal */}
      {showExtractorModal && (
        <AuthProfileExtractorModal
          onClose={() => setShowExtractorModal(false)}
          onTokenExtracted={handleTokenExtracted}
          projectId={webTarget?.project}
        />
      )}

      {/* Create Profile From Captured Login Modal */}
      {showCaptureModal && (
        <CreateProfileFromCaptureModal
          webTarget={webTarget}
          isDarkMode={isDarkMode}
          onClose={() => setShowCaptureModal(false)}
          onCreated={loadProfiles}
        />
      )}

      {/* Login Detection Settings Modal */}
      {showLoginSettings && (
        <LoginDetectionSettings
          webTargetId={webTarget?._id}
          isDarkMode={isDarkMode}
          onClose={() => setShowLoginSettings(false)}
        />
      )}

      {/* Workflow Viewer Modal */}
      {showWorkflowViewer && workflowViewerProfileId && (
        <LoginWorkflowViewer
          profileId={workflowViewerProfileId}
          isDarkMode={isDarkMode}
          onClose={() => {
            setShowWorkflowViewer(false);
            setWorkflowViewerProfileId(null);
          }}
        />
      )}

      {/* Manual Credential Mapper Modal */}
      {showCredentialMapper && credentialMapperProfileId && (
        <ManualCredentialMapper
          profileId={credentialMapperProfileId}
          isDarkMode={isDarkMode}
          onClose={() => {
            setShowCredentialMapper(false);
            setCredentialMapperProfileId(null);
          }}
          onMappingComplete={handleMappingComplete}
        />
      )}

      {/* TOTP Configuration Modal */}
      {showTOTPConfig && totpProfileId && (
        <TOTPConfiguration
          profileId={totpProfileId}
          isDarkMode={isDarkMode}
          onClose={() => {
            setShowTOTPConfig(false);
            setTOTPProfileId(null);
          }}
          onConfigured={async () => {
            await loadProfiles();
            if (totpProfileId) {
              const updatedProfile = await authProfileService.getById(totpProfileId);
              handleEdit(updatedProfile);
            }
          }}
        />
      )}

      {/* Duplicate Profile Confirmation Modal */}
      {showDuplicateConfirm && duplicateInfo && (
        <ProfileMergeConfirm
          duplicateInfo={duplicateInfo}
          isDarkMode={isDarkMode}
          onClose={() => {
            setShowDuplicateConfirm(false);
            setDuplicateInfo(null);
          }}
          onResolved={handleDuplicateResolved}
        />
      )}
    </div>
  );
};

export default AuthProfileManager;
